<template>

    <div class="shop-filter-triggers">
        <a class="shop-filter-trigger" href="#" data-ec-toggle="offcanvas" data-ec-target="#shop-sort">
            <svg focusable="false" role="img">
                <use xlink:href="/build/assets/icons.svg#sort" href="/build/assets/icons.svg#sort" />
            </svg>
            Sort By
        </a>
        <a class="shop-filter-trigger" href="#" data-ec-toggle="offcanvas" data-ec-target="#shop-filter">
            <svg focusable="false" role="img">
                <use xlink:href="/build/assets/icons.svg#refine" href="/build/assets/icons.svg#refine" />
            </svg>
            Refine
        </a>
    </div>

    <!-- Mobile Search (full width, same level as filter triggers) -->
    <div v-if="shouldShowSearch" class="mobile-search-wrapper">
        <div class="search-input-wrapper">
            <svg class="search-icon" focusable="false" role="img" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
                type="text"
                class="form-control search-input-with-icon"
                placeholder="Search products..."
                v-model="searchQuery"
                @keyup="debouncedSearchHandler"
                @keyup.enter="handleSearch(searchQuery)"
            />
            <button
                v-if="searchQuery"
                type="button"
                class="search-clear-btn"
                @click="clearSearch"
                aria-label="Clear search"
            >
                &times;
            </button>
        </div>
    </div>

    <div class="container">
        <div class="row justify-content-between shop-main-row">
            <div class="col-3 shop-sidebar">
                <CollectionFilters
                    data-vue
                    :filters="filters"
                    :collection="collection"
                    :group="group"
                    :selected_filters="selected"
                    :show-search="shouldShowSearch"
                    :initial_search="searchQuery"
                    @update="updateFilters"
                    @clear="clearAllFilters"
                    @search="handleSearch"
                />

            </div>
            <div ref="listingTop" class="col-8 shop-main">
                <!-- Shop Banner -->
                <div v-if="shopBanner.enabled && (shopBanner.heading || shopBanner.content)" class="shop-banner bg-light py-4 mb-4 rounded">
                    <h2 v-if="shopBanner.heading" class="shop-banner-heading">{{ shopBanner.heading }}</h2>
                    <div v-if="shopBanner.content" class="shop-banner-content" v-html="shopBanner.content"></div>
                </div>

                <div v-if="activeFilters.length" class="shop-filter-selections">
                    <div class="shop-filter-selections__list">
                        <button
                            v-for="filter in activeFilters"
                            :key="`${filter.groupKey}-${filter.value}`"
                            type="button"
                            class="shop-filter-selections__item btn btn-sm btn-outline-primary"
                            @click="removeFilter(filter.groupKey, filter.value)"
                        >
                            <span>{{ filter.groupLabel }}: {{ filter.valueLabel }}</span>
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <button
                            type="button"
                            class="shop-filter-selections__clear btn btn-sm btn-text"
                            @click="clearAllFilters"
                        >
                            Clear Filters
                        </button>

                    </div>
                </div>

                <div v-if="isLoading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else>
                    <ProductGrids
                        data-vue
                        :products="products"
                        :is-brochure-mode="isBrochureMode"
                        :show-prices="showPrices"
                        :show-botanical="showBotanical"
                        :show-common-name-subtitle="showCommonNameSubtitle"
                        :default-image="props.defaultImage"
                    />
                    <div v-if="products?.length === 0" class="text-center py-5">
                        <p>No products found.</p>
                    </div>

                    <!-- Pagination Controls -->
                    <nav v-if="totalPages > 1" class="posts-load-more" aria-label="Collection pagination">
                        <div class="btn-group" role="group" aria-label="Pagination">
                            <button
                                class="btn btn-outline-primary btn-prev"
                                @click="prevPage"
                                :disabled="currentPage <= 1"
                                :class="{ disabled: currentPage <= 1 }"
                            >
                                <span>Previous</span>
                            </button>

                            <button
                                v-for="item in paginationItems"
                                :key="item.key"
                                class="btn"
                                :class="item.type === 'page'
                                    ? (item.value === currentPage ? 'btn-primary' : 'btn-outline-primary')
                                    : 'btn-outline-primary disabled btn-ellipsis'"
                                :disabled="item.type !== 'page'"
                                @click="item.type === 'page' ? goToPage(item.value) : null"
                            >
                                {{ item.label }}
                            </button>

                            <button
                                class="btn btn-outline-primary btn-next"
                                @click="nextPage"
                                :disabled="!hasMorePages"
                                :class="{ disabled: !hasMorePages }"
                            >
                                <span>Next</span>
                            </button>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>

import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import ProductGrids from './../Product/ProductGrids.vue'
import CollectionFilters from './/CollectionFilters.vue'

const props = defineProps({
    selectedFilters: Object,
    showSearch: {
        type: [Boolean, String],
        default: true
    },
    sort: {
        type: String,
        default: 'title_asc'
    },
    isBrochureMode: {
        type: [Boolean, String],
        default: false
    },
    showPrices: {
        type: [Boolean, String],
        default: true
    },
    showBotanical: {
        type: [Boolean, String],
        default: false
    },
    showCommonNameSubtitle: {
        type: [Boolean, String],
        default: false
    },
    defaultImage: {
        type: String,
        default: '/images/default.jpg'
    }
})

const visitorStorageKey = 'eg_search_visitor_id'

function generateVisitorToken() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID()
    }

    return `search-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

function getVisitorToken() {
    try {
        let token = window.localStorage.getItem(visitorStorageKey)

        if (!token) {
            token = generateVisitorToken()
            window.localStorage.setItem(visitorStorageKey, token)
        }

        return token
    } catch (error) {
        return ''
    }
}

// Convert string props to boolean
const isBrochureMode = computed(() => {
    return props.isBrochureMode === true || props.isBrochureMode === 'true'
})

const showPrices = computed(() => {
    return props.showPrices === true || props.showPrices === 'true'
})

const showCommonNameSubtitle = computed(() => {
    return props.showCommonNameSubtitle === true || props.showCommonNameSubtitle === 'true'
})

const shouldShowSearch = computed(() => {
    return props.showSearch === true || props.showSearch === 'true'
})

// Parse URL for collection and group IDs
const pathParts = window.location.pathname.split('/').filter(Boolean)
const collectionId = pathParts[0] === 'collections' ? pathParts[1] : 0
const groupId = pathParts.length > 2 ? pathParts[2] : null

// Get initial search query from URL
const urlParams = new URLSearchParams(window.location.search)
const initialSearchQuery = urlParams.get('s') || ''

const { parsedInitialProducts, parsedInitialMeta } = (() => {
    try {
        const el = document.getElementById('initial-products-data')
        if (!el) {
            return { parsedInitialProducts: null, parsedInitialMeta: null }
        }

        const payload = JSON.parse(el.textContent)

        return {
            parsedInitialProducts: Array.isArray(payload.data) ? payload.data : null,
            parsedInitialMeta: payload.meta ?? null,
        }
    } catch {
        return { parsedInitialProducts: null, parsedInitialMeta: null }
    }
})()

// Reactive refs
const selected = ref({ ...props.selectedFilters })
const products = ref(parsedInitialProducts ?? [])
const filters = ref([])
const collection = ref(null)
const group = ref(null)
const sort = ref(props.sort || 'title_asc')
const searchQuery = ref(initialSearchQuery)
const isLoading = ref(!parsedInitialProducts)
const currentPage = ref(parsedInitialMeta?.current_page ?? 1)
const totalPages = ref(parsedInitialMeta?.last_page ?? 1)
const hasMorePages = ref((parsedInitialMeta?.current_page ?? 1) < (parsedInitialMeta?.last_page ?? 1))
const listingTop = ref(null)

// Shop Banner data from server-side
const shopBanner = ref({
    enabled: false,
    heading: '',
    content: ''
})

const formatGroupLabel = (key) => {
    if (filters.value?.custom_names && filters.value.custom_names[key.toLowerCase()]) {
        return filters.value.custom_names[key.toLowerCase()]
    }

    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
}

const formatValueLabel = (value) => {
    return filters.value?.labels?.[value] || value
}

const activeFilters = computed(() => {
    return Object.entries(selected.value).flatMap(([groupKey, values]) => {
        if (!Array.isArray(values) || !values.length) {
            return []
        }

        return values.map((value) => ({
            groupKey,
            value,
            groupLabel: formatGroupLabel(groupKey),
            valueLabel: formatValueLabel(value),
        }))
    })
})

const paginationItems = computed(() => {
    const total = totalPages.value
    const current = currentPage.value

    if (total <= 7) {
        return Array.from({ length: total }, (_, index) => ({
            type: 'page',
            value: index + 1,
            label: index + 1,
            key: `page-${index + 1}`,
        }))
    }

    const pages = new Set([1, total, current])

    if (current > 1) {
        pages.add(current - 1)
    }

    if (current < total) {
        pages.add(current + 1)
    }

    if (current <= 3) {
        pages.add(2)
        pages.add(3)
    }

    if (current >= total - 2) {
        pages.add(total - 1)
        pages.add(total - 2)
    }

    const sortedPages = [...pages]
        .filter(page => page >= 1 && page <= total)
        .sort((a, b) => a - b)

    const items = []

    sortedPages.forEach((page, index) => {
        if (index > 0) {
            const previousPage = sortedPages[index - 1]

            if (page - previousPage > 1) {
                items.push({
                    type: 'ellipsis',
                    label: '...',
                    key: `ellipsis-${previousPage}-${page}`,
                })
            }
        }

        items.push({
            type: 'page',
            value: page,
            label: page,
            key: `page-${page}`,
        })
    })

    return items
})

const buildUrl = () => {
    const base = '/api/collections/fetch'
    return groupId ? `${base}/${collectionId}/${groupId}` : `${base}/${collectionId}`
}

const logResultsPageSearch = async (items, meta) => {
    const trimmedQuery = searchQuery.value.trim()

    if (trimmedQuery === '') {
        return
    }

    const visitorToken = getVisitorToken()

    if (visitorToken === '') {
        return
    }

    try {
        await axios.post('/api/search/results-page', {
            query: trimmedQuery,
            visitor_token: visitorToken,
            results_count: Number(meta?.total ?? items.length ?? 0),
            page_number: Number(meta?.current_page ?? currentPage.value ?? 1),
            results: (Array.isArray(items) ? items : []).map((item) => ({
                id: item.id ?? null,
                handle: item.handle ?? '',
                title: item.title ?? '',
            })),
        })
    } catch (error) {
        console.error('Error logging results-page search:', error)
    }
}

const fetchProducts = async (page = 1) => {
    const params = new URLSearchParams()

    Object.entries(selected.value).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length) {
            params.append(`_ft[${key}]`, values.join(','))
        }
    })

    if (sort.value) {
        params.append('sort', sort.value)
    }

    // Add search query if present
    if (searchQuery.value && searchQuery.value.trim() !== '') {
        params.append('s', searchQuery.value.trim())
    }

    // Add page parameter
    if (page > 1) {
        params.append('page', page)
    }

    try {
        const response = await axios.get(`${buildUrl()}?${params.toString()}`)
        const data = response.data
        let nextProducts = []

        // Handle both direct array and paginated resource collection formats
        if (Array.isArray(data)) {
            nextProducts = data
        } else if (data.data && Array.isArray(data.data)) {
            nextProducts = data.data
        }

        products.value = nextProducts

        // Update pagination info from meta object
        if (data.meta) {
            currentPage.value = data.meta.current_page || 1
            totalPages.value = data.meta.last_page || 1
            hasMorePages.value = data.meta.current_page < data.meta.last_page
        } else {
            // Fallback if no meta object
            currentPage.value = 1
            totalPages.value = 1
            hasMorePages.value = false
        }

        await logResultsPageSearch(products.value, data.meta ?? null)
    } catch (err) {
        console.error('Error loading filtered products:', err)
    }
}

const fetchData = async () => {
    const base = `/api/collections/filters/${groupId ? `${collectionId}/${groupId}` : collectionId}`

    // Add search query if present
    const params = new URLSearchParams()
    if (searchQuery.value && searchQuery.value.trim() !== '') {
        params.append('s', searchQuery.value.trim())
    }

    try {
        const url = params.toString() ? `${base}?${params.toString()}` : base
        const { data } = await axios.get(url)
        collection.value = data.collection
        group.value = data.group
        filters.value = data.filters || []
        // Don't set products here - let fetchProducts() handle it with proper sorting
    } catch (e) {
        console.error('Failed to load data:', e)
    }
}

const updateFilters = (category, value) => {
    if (category === 'sort') {
        sort.value = value
    } else {
        const current = selected.value[category] || []
        const index = current.indexOf(value)
        if (index > -1) {
            current.splice(index, 1)
        } else {
            current.push(value)
        }
        selected.value[category] = current
    }

    // Reset to page 1 when filters change
    currentPage.value = 1
    fetchProducts(1)
}

const removeFilter = (category, value) => {
    updateFilters(category, value)
}

const clearAllFilters = () => {
    Object.keys(selected.value).forEach(key => {
        selected.value[key] = []
    })
    searchQuery.value = ''
    // Reset to page 1 when clearing filters
    currentPage.value = 1
    fetchProducts(1)
}

const handleSearch = (query) => {
    searchQuery.value = query
    // Reset to page 1 when searching
    currentPage.value = 1
    // Refetch filters and products with new search query
    fetchData()
    fetchProducts(1)
}

// Debounce helper
const debounce = (fn, delay) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), delay);
    };
};

// Auto-search after 300ms of no typing
const debouncedSearchHandler = debounce(() => {
    handleSearch(searchQuery.value);
}, 300);

const clearSearch = () => {
    searchQuery.value = ''
    handleSearch('')
}

const scrollToListingTop = () => {
    if (!listingTop.value) {
        return
    }

    const offsetTop = listingTop.value.getBoundingClientRect().top + window.scrollY - 24

    window.scrollTo({
        top: Math.max(0, offsetTop),
        behavior: 'smooth',
    })
}

const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        scrollToListingTop()
        fetchProducts(page)
    }
}

const nextPage = () => {
    if (hasMorePages.value) {
        goToPage(currentPage.value + 1)
    }
}

const prevPage = () => {
    if (currentPage.value > 1) {
        goToPage(currentPage.value - 1)
    }
}

onMounted(async () => {
    try {
        getVisitorToken()

        // Initialize shop banner from data attributes
        const vueElement = document.querySelector('[data-vue="collection-list"]')
        if (vueElement) {
            shopBanner.value = {
                enabled: vueElement.dataset.shopBannerEnabled === 'true',
                heading: vueElement.dataset.shopBannerHeading || '',
                content: vueElement.dataset.shopBannerContent || ''
            }
        }

        await fetchData()
        if (!parsedInitialProducts) {
            await fetchProducts()
        } else if (initialSearchQuery.trim() !== '') {
            await logResultsPageSearch(parsedInitialProducts, parsedInitialMeta)
        }
    } finally {
        isLoading.value = false
    }
})
</script>
